import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  billingAPI,
  type BalanceTransactionItem,
  type CameraBilling,
  type PublicUser,
} from '../../utils/api';
import CameraSubscriptions from './CameraSubscriptions';
import TopUpModal, { PENDING_PAYMENT_KEY, type YooKassaPaymentMethod } from './TopUpModal';
import {
  YOOKASSA_PAYMENT_METHODS,
  readPreferredPaymentMethod,
  writePreferredPaymentMethod,
  yooKassaMethodOption,
} from './yookassaPaymentMethods';
import './Balance.css';

const TRANSACTIONS_PAGE_SIZE = 4;

function formatMoney(value: number): string {
  const prefix = value > 0 ? '+ ' : value < 0 ? '- ' : '';
  return `${prefix}${Math.abs(value).toLocaleString('ru-RU')} ₽`;
}

function formatDateTime(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value.replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatShortDate(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

const Balance: React.FC = () => {
  const { setUser } = useAuth();

  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(0);
  /** Ближайшее автосписание за камеры: дата и сумма (камеры, продлевающиеся в этот день). */
  const [nextCharge, setNextCharge] = useState<{ date: string; amount: number } | null>(null);
  const [yookassaEnabled, setYookassaEnabled] = useState(false);
  const [transactions, setTransactions] = useState<BalanceTransactionItem[]>([]);
  const [transactionsTotal, setTransactionsTotal] = useState(0);
  const [transactionsHasMore, setTransactionsHasMore] = useState(false);
  const [transactionsLoadingMore, setTransactionsLoadingMore] = useState(false);
  const [isTopUpOpen, setIsTopUpOpen] = useState(false);
  const [topUpPaymentMethod, setTopUpPaymentMethod] = useState<YooKassaPaymentMethod | null>(null);
  const [preferredMethod, setPreferredMethod] = useState<YooKassaPaymentMethod | null>(() =>
    readPreferredPaymentMethod()
  );
  const [showAllPaymentMethods, setShowAllPaymentMethods] = useState(false);
  const [error, setError] = useState('');

  const selectPreferredMethod = (method: YooKassaPaymentMethod | null) => {
    setPreferredMethod(method);
    writePreferredPaymentMethod(method);
  };

  const openTopUp = (method?: YooKassaPaymentMethod | null) => {
    const next = method === undefined ? preferredMethod : method;
    if (method !== undefined) {
      selectPreferredMethod(method);
    }
    setTopUpPaymentMethod(next);
    setIsTopUpOpen(true);
  };

  const closeTopUp = () => {
    setIsTopUpOpen(false);
    setTopUpPaymentMethod(null);
  };

  const loadSummary = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await billingAPI.getSummary();
      if (response.success) {
        setBalance(response.balance);
        setYookassaEnabled(!!response.yookassa_enabled);
        setTransactions(response.transactions);
        setTransactionsTotal(response.transactions_total);
        setTransactionsHasMore(response.transactions_total > response.transactions.length);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Не удалось загрузить данные баланса');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const syncPendingPayment = async () => {
      const paymentId = sessionStorage.getItem(PENDING_PAYMENT_KEY);
      if (!paymentId) {
        void loadSummary();
        return;
      }

      try {
        const synced = await billingAPI.syncYooKassaPayment(paymentId);
        if (synced.success) {
          setBalance(synced.balance);
          if (synced.user) {
            setUser(synced.user);
          }
        }
      } catch {
        // webhook мог ещё не дойти — покажем текущий баланс
      } finally {
        sessionStorage.removeItem(PENDING_PAYMENT_KEY);
        void loadSummary();
      }
    };

    void syncPendingPayment();
  }, [loadSummary, setUser]);

  const loadMoreTransactions = async () => {
    try {
      setTransactionsLoadingMore(true);
      const response = await billingAPI.getTransactions({
        limit: TRANSACTIONS_PAGE_SIZE,
        offset: transactions.length,
      });
      if (response.success) {
        setTransactions((prev) => [...prev, ...response.transactions]);
        setTransactionsHasMore(response.has_more);
        setTransactionsTotal(response.total);
      }
    } finally {
      setTransactionsLoadingMore(false);
    }
  };

  const handleCamerasChanged = useCallback((cameras: CameraBilling[], price: number) => {
    const renewing = cameras.filter((c) => c.status === 'active' && c.auto_renew && c.paid_until);
    if (renewing.length === 0) {
      setNextCharge(null);
      return;
    }
    const dayKey = (iso: string) => new Date(iso).toDateString();
    const earliest = renewing
      .map((c) => c.paid_until as string)
      .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())[0];
    const sameDay = renewing.filter((c) => dayKey(c.paid_until as string) === dayKey(earliest)).length;
    setNextCharge({ date: earliest, amount: sameDay * price });
  }, []);

  const handleTopUpSuccess = (nextBalance: number, updatedUser?: PublicUser) => {
    setBalance(nextBalance);
    if (updatedUser) {
      setUser(updatedUser);
    }
    void loadSummary();
  };

  return (
    <div className="balance-page">

      <main className="balance-main">
        <div className="container">
          <nav className="balance-breadcrumbs" aria-label="Навигация">
            <Link to="/dashboard">Главная</Link>
            <span aria-hidden="true">/</span>
            <span>Баланс</span>
          </nav>

          <header className="balance-header">
            <h1 className="balance-header__title">Баланс</h1>
            <p className="balance-header__subtitle">
              Управляйте балансом, просматривайте платежи и историю расходов
            </p>
          </header>

          {error && <p className="balance-error">{error}</p>}

          <div className="balance-grid">
            <section className="balance-card balance-card--current">
              <div className="balance-card__top">
                <div>
                  <h2 className="balance-card__label">Текущий баланс</h2>
                  <p className="balance-card__amount">
                    {loading ? '—' : `${balance.toLocaleString('ru-RU')} ₽`}
                  </p>
                  <p className="balance-card__hint">Доступно для оплаты и услуг</p>
                </div>
                <button
                  type="button"
                  className="balance-card__primary-btn"
                  onClick={() => openTopUp()}
                >
                  Пополнить баланс
                </button>
              </div>

              <div className="balance-card__meta">
                <div className="balance-card__meta-item">
                  <span className="balance-card__meta-label">Автопополнение</span>
                  <span className="balance-card__meta-value">Выкл</span>
                </div>
                <div className="balance-card__meta-item">
                  <span className="balance-card__meta-label">Следующее списание</span>
                  <span className="balance-card__meta-value">
                    {nextCharge
                      ? `${formatShortDate(nextCharge.date)} · ${nextCharge.amount.toLocaleString('ru-RU')} ₽`
                      : '—'}
                  </span>
                </div>
              </div>
            </section>

            <section className="balance-card balance-card--payments">
              <div className="balance-card__row-header">
                <h2 className="balance-card__title">Способы оплаты</h2>
                <button
                  type="button"
                  className="balance-card__link-btn"
                  onClick={() => openTopUp()}
                  disabled={!yookassaEnabled || loading}
                >
                  + Пополнить
                </button>
              </div>

              {loading ? (
                <p className="balance-card__loading">Загрузка…</p>
              ) : yookassaEnabled ? (
                <>
                  <ul className="balance-payments">
                    {(showAllPaymentMethods
                      ? [
                          { type: null as YooKassaPaymentMethod | null },
                          ...YOOKASSA_PAYMENT_METHODS.map((m) => ({
                            type: m.type as YooKassaPaymentMethod | null,
                          })),
                        ]
                      : [{ type: preferredMethod }]
                    ).map(({ type }) => {
                      const option = yooKassaMethodOption(type);
                      const isActive = type === preferredMethod;
                      return (
                        <li key={type ?? 'yookassa'}>
                          <button
                            type="button"
                            className={`balance-payment${isActive ? ' balance-payment--active' : ''}`}
                            onClick={() => {
                              if (showAllPaymentMethods) {
                                selectPreferredMethod(type);
                                setShowAllPaymentMethods(false);
                                return;
                              }
                              openTopUp(type);
                            }}
                          >
                            <span
                              className={`balance-payment__logo ${option.logoClass}`.trim()}
                              aria-hidden="true"
                            >
                              {option.logoText}
                            </span>
                            <span className="balance-payment__content">
                              <span className="balance-payment__name">{option.name}</span>
                              <span className="balance-payment__meta">{option.meta}</span>
                            </span>
                            {isActive && (
                              <span className="balance-payment__badge">Активно</span>
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                  <button
                    type="button"
                    className="balance-card__footer-link"
                    onClick={() => setShowAllPaymentMethods((prev) => !prev)}
                  >
                    {showAllPaymentMethods ? 'Свернуть' : 'Все способы оплаты'}
                    <span aria-hidden="true">{showAllPaymentMethods ? '⌃' : '⌄'}</span>
                  </button>
                </>
              ) : (
                <div className="balance-payments-empty">
                  Платёжная система временно недоступна
                </div>
              )}
            </section>

            <section className="balance-card balance-card--transactions">
              <div className="balance-card__row-header">
                <h2 className="balance-card__title">История транзакций</h2>
                <button
                  type="button"
                  className="balance-card__link-btn"
                  onClick={() => void loadMoreTransactions()}
                  disabled={transactionsLoadingMore || transactionsTotal === 0}
                >
                  Все транзакции
                </button>
              </div>

              {loading ? (
                <p className="balance-card__loading">Загрузка…</p>
              ) : transactions.length === 0 ? (
                <p className="balance-card__empty">Транзакций пока нет</p>
              ) : (
                <ul className="balance-transactions">
                  {transactions.map((tx) => (
                    <li key={tx.id} className="balance-transaction">
                      <div className="balance-transaction__icon" aria-hidden="true">
                        <img src={`${process.env.PUBLIC_URL}/icons/modals/people.svg`} alt="" />
                      </div>
                      <div className="balance-transaction__content">
                        <span className="balance-transaction__title">{tx.description}</span>
                        <span className="balance-transaction__date">{formatDateTime(tx.created_at)}</span>
                      </div>
                      <div className="balance-transaction__right">
                        <span
                          className={`balance-transaction__amount ${
                            tx.amount >= 0 ? 'balance-transaction__amount--plus' : 'balance-transaction__amount--minus'
                          }`}
                        >
                          {formatMoney(tx.amount)}
                        </span>
                        <span className="balance-transaction__status">Успешно</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              {transactionsHasMore && !loading && (
                <button
                  type="button"
                  className="balance-card__more"
                  onClick={() => void loadMoreTransactions()}
                  disabled={transactionsLoadingMore}
                >
                  {transactionsLoadingMore ? 'Загрузка…' : (
                    <>
                      Показать еще
                      <span aria-hidden="true">⌄</span>
                    </>
                  )}
                </button>
              )}
            </section>

            <div className="balance-side">
              <CameraSubscriptions
                onBalanceChanged={(updatedUser) => {
                  setUser(updatedUser);
                  if (typeof updatedUser.balance === 'number') setBalance(updatedUser.balance);
                  void loadSummary();
                }}
                onTopUp={() => openTopUp()}
                onCamerasChanged={handleCamerasChanged}
              />

              <section className="balance-card balance-card--docs">
                <h2 className="balance-card__title balance-card__title--small">
                  Счета и закрывающие документы
                </h2>
                <p className="balance-card__text">
                  Получайте акты выполненных работ и закрывающие документы по оплатам
                </p>
                <button type="button" className="balance-card__nav-link" disabled>
                  Перейти к документам
                  <span aria-hidden="true">›</span>
                </button>
              </section>
            </div>
          </div>
        </div>
      </main>

      {isTopUpOpen && (
        <TopUpModal
          currentBalance={balance}
          paymentMethodType={topUpPaymentMethod}
          onClose={closeTopUp}
          onSuccess={handleTopUpSuccess}
        />
      )}

    </div>
  );
};

export default Balance;
